// pages/RegisterPage.jsx
import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import API from '../api';
import { useNavigate, Link } from 'react-router-dom';
import '../styles/global.css';

export default function RegisterPage() {
  const [fullName, setFullName] = useState('');
  const [pharmacyName, setPharmacyName] = useState('');
  const [phone, setPhone] = useState('');
  const [pan, setPan] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [touched, setTouched] = useState({});
  const [mounted, setMounted] = useState(false);
  const [focusedField, setFocusedField] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  
  // ✅ NEW: Show success overlay
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    setMounted(true);
  }, []);

  // --- VALIDATION LOGIC ---
  const panValid = /^\d{9}$/.test(pan.trim());
  const phoneValid = /^\d{7,15}$/.test(phone.trim());
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const passwordValid = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/.test(password);
  const confirmMatches = password === confirm && confirm.length > 0;

  const step1Valid = fullName.trim().length > 0 && pharmacyName.trim().length > 0 && panValid && phoneValid;
  const step2Valid = emailValid && passwordValid && confirmMatches;

  // Password Strength Calculator
  const passwordStrength = useMemo(() => {
    if (!password) return { score: 0, label: '', color: '#e0e0e0', percent: 0 };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^\w\s]/.test(password)) score++;

    const levels = [
      { min: 0, label: 'Very Weak', color: '#ef4444' },
      { min: 2, label: 'Weak', color: '#f97316' },
      { min: 3, label: 'Fair', color: '#eab308' },
      { min: 4, label: 'Good', color: '#22c55e' },
      { min: 5, label: 'Strong', color: '#16a34a' },
      { min: 6, label: 'Excellent', color: '#059669' },
    ];
    const level = [...levels].reverse().find(l => score >= l.min) || levels[0];
    return { score, ...level, percent: (score / 6) * 100 };
  }, [password]);

  const touch = (field) => setTouched((t) => ({ ...t, [field]: true }));

  const handleNextStep = (e) => {
    e.preventDefault();
    setTouched({ fullName: true, pharmacyName: true, phone: true, pan: true });
    
    if (step1Valid) {
      setCurrentStep(2);
      // ✅ Show toast for step completion
      toast.info('Step 1 completed! Now set up your security details.');
    } else {
      toast.error('Please fill in all fields correctly.');
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setTouched({ fullName: true, pharmacyName: true, pan: true, phone: true, email: true, password: true, confirm: true });

    if (!step1Valid || !step2Valid || loading) {
      toast.error('Please fill in all fields correctly.');
      return;
    }
    
    setLoading(true);

    try {
      const { data } = await API.post('/users/register', {
        name: fullName.trim(),
        pharmacyName: pharmacyName.trim(),
        phone: phone.trim(),
        pan: pan.trim().toUpperCase(),
        email: email.trim().toLowerCase(),
        password,
      });

      // ✅ Store success message from backend or use default
      const message = data.message || 'Account created successfully! Please verify your email.';
      setSuccessMessage(message);
      setSuccess(message);
      
      // ✅ Show toast notification
      toast.success(message);
      
      // ✅ Show success overlay
      setShowSuccessOverlay(true);
      setLoading(false);
      
      // ✅ Navigate after showing success
      setTimeout(() => {
        navigate('/verify-otp', { 
          state: { 
            email: data.email,
            message: 'Please enter the OTP sent to your email.'
          } 
        });
      }, 2000);
      
    } catch (err) {
      setLoading(false);
      const errorMessage = err.response?.data?.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      
      // ✅ Show toast for error
      toast.error(errorMessage);
      
      setTimeout(() => setError(''), 5000);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; }
        .page-container {
          min-height: 100vh;
          display: flex;
          font-family: 'Poppins', sans-serif;
          background-color: #1a2b3c;
          background-image: radial-gradient(circle at 80% 10%, rgba(4, 159, 137, 0.15) 0%, transparent 25%);
          overflow: hidden;
          position: relative;
        }

        .mobile-header { display: none; }

        @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes checkmark { 0% { transform: scale(0); } 50% { transform: scale(1.2); } 100% { transform: scale(1); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        
        .register-input::placeholder { color: rgba(255,255,255,0.4); }
        
        .register-input:focus {
           border-color: #81e6d9 !important;
           box-shadow: 0 0 0 4px rgba(129, 230, 217, 0.15);
        }

        input:-webkit-autofill,
        input:-webkit-autofill:hover, 
        input:-webkit-autofill:focus {
          -webkit-text-fill-color: white;
          -webkit-box-shadow: 0 0 0px 1000px #233446 inset;
          transition: background-color 5000s ease-in-out 0s;
        }

        @media (max-width: 900px) {
          .page-container {
            flex-direction: column;
            overflow-y: auto;
          }
          .left-panel { display: none !important; }
          .right-panel { 
            width: 100% !important; 
            padding: 20px !important;
            display: flex;
            flex-direction: column;
            justify-content: flex-start !important;
            padding-top: 40px !important;
          }
          .mobile-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            margin-bottom: 30px;
            width: 100%;
          }
          .form-card {
            margin-bottom: 40px;
          }
        }
      `}</style>

      <div className="page-container">
        {/* LEFT PANEL (Desktop Only) */}
        <div className="left-panel" style={styles.leftPanel}>
          <div style={styles.illustrationContainer}>
            <img 
              src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/People/Woman%20Health%20Worker.png" 
              alt="Register 3D" 
              style={styles.heroImage}
            />
            <div style={styles.heroTextContainer}>
              <h1 style={styles.heroTitle}>Join the Future<br/>of Pharmacy</h1>
              <div style={styles.benefitsGrid}>
                {['Smart Ledger', 'Inventory Sync', 'Secure Data', 'Easy Billing'].map((item, i) => (
                  <div key={i} style={styles.benefitTag}>✓ {item}</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL (Form & Mobile Header) */}
        <div className="right-panel" style={styles.rightPanel}>
          
          {/* MOBILE HEADER (Visible only on Mobile) */}
          <div className="mobile-header">
             <img 
               src="https://raw.githubusercontent.com/Tarikul-Islam-Anik/Animated-Fluent-Emojis/master/Emojis/People/Woman%20Health%20Worker.png" 
               alt="Logo" 
               style={{ width: '80px', marginBottom: '15px' }} 
             />
             <div style={{ color: '#81e6d9', fontWeight: 'bold', fontSize: '24px', marginBottom: '5px' }}>
                PharmaLedger
             </div>
             <p style={{ color: '#a0aec0', fontSize: '14px', margin: 0 }}>
                Join the future of pharmacy management.
             </p>
          </div>

          {/* ✅ SUCCESS OVERLAY */}
          {showSuccessOverlay && (
            <div style={styles.successOverlay}>
              <div style={styles.successCard}>
                <div style={styles.checkIcon}>✓</div>
                <h3 style={styles.successTitle}>{successMessage}</h3>
                <p style={styles.successSubtitle}>Redirecting to verification...</p>
                <div style={styles.emailBadge}>
                  📧 OTP sent to {email}
                </div>
              </div>
            </div>
          )}

          <div style={{
            ...styles.formCard,
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
          }} className="form-card">
            <div style={styles.headerSection}>
              <h2 style={styles.headerTitle}>Register</h2>
              <div style={styles.headerLine} />
              <p style={styles.headerSubtitle}>Create your account in 2 steps</p>
            </div>

            {/* Step Indicators */}
            <div style={styles.stepContainer}>
              <div style={currentStep === 1 ? styles.stepActive : styles.stepCompleted}>
                {currentStep > 1 ? '✓' : '1.'} Details
              </div>
              <div style={styles.stepLine} />
              <div style={currentStep === 2 ? styles.stepActive : styles.stepInactive}>
                2. Security
              </div>
            </div>

            {/* ✅ ERROR MESSAGE */}
            {error && (
              <div style={styles.errorBox}>
                <span>⚠️</span> {error}
              </div>
            )}
            
            {/* ✅ SUCCESS MESSAGE (inline) */}
            {success && !showSuccessOverlay && (
              <div style={styles.successBox}>
                <span>✓</span> {success}
              </div>
            )}

            <form onSubmit={currentStep === 1 ? handleNextStep : submit}>
              {currentStep === 1 ? (
                <div style={{ animation: 'slideIn 0.3s ease' }}>
                  {/* Full Name */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Full Name</label>
                    <div style={styles.inputWrapper(focusedField === 'fullName', touched.fullName && !fullName.trim())}>
                      <input
                        type="text"
                        className="register-input"
                        placeholder="Your Name"
                        value={fullName}
                        onChange={(e) => { setFullName(e.target.value); setError(''); }}
                        onFocus={() => setFocusedField('fullName')}
                        onBlur={() => { setFocusedField(''); touch('fullName'); }}
                        style={styles.input}
                      />
                    </div>
                    {touched.fullName && !fullName.trim() && (
                      <div style={styles.fieldError}>• Name is required</div>
                    )}
                  </div>

                  {/* Pharmacy Name */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Pharmacy Name</label>
                    <div style={styles.inputWrapper(focusedField === 'pharmacyName', touched.pharmacyName && !pharmacyName.trim())}>
                      <input
                        type="text"
                        className="register-input"
                        placeholder="Shop Name"
                        value={pharmacyName}
                        onChange={(e) => { setPharmacyName(e.target.value); setError(''); }}
                        onFocus={() => setFocusedField('pharmacyName')}
                        onBlur={() => { setFocusedField(''); touch('pharmacyName'); }}
                        style={styles.input}
                      />
                    </div>
                    {touched.pharmacyName && !pharmacyName.trim() && (
                      <div style={styles.fieldError}>• Pharmacy name is required</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ ...styles.inputGroup, flex: 1 }}>
                      <label style={styles.label}>Phone</label>
                      <div style={styles.inputWrapper(focusedField === 'phone', touched.phone && !phoneValid)}>
                        <input
                          type="tel"
                          className="register-input"
                          placeholder="Phone No"
                          value={phone}
                          onChange={(e) => { setPhone(e.target.value.replace(/\D/g, '').slice(0, 15)); setError(''); }}
                          onFocus={() => setFocusedField('phone')}
                          onBlur={() => { setFocusedField(''); touch('phone'); }}
                          style={styles.input}
                        />
                      </div>
                      {touched.phone && !phoneValid && (
                        <div style={styles.fieldError}>• Invalid phone number</div>
                      )}
                    </div>
                    
                    <div style={{ ...styles.inputGroup, flex: 1 }}>
                      <label style={styles.label}>PAN (9 digits)</label>
                      <div style={styles.inputWrapper(focusedField === 'pan', touched.pan && !panValid)}>
                        <input
                          type="text"
                          className="register-input"
                          placeholder="PAN"
                          value={pan}
                          onChange={(e) => { setPan(e.target.value.replace(/\D/g, '').slice(0, 9)); setError(''); }}
                          onFocus={() => setFocusedField('pan')}
                          onBlur={() => { setFocusedField(''); touch('pan'); }}
                          style={styles.input}
                        />
                      </div>
                      {touched.pan && !panValid && (
                        <div style={styles.fieldError}>• PAN must be 9 digits</div>
                      )}
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    disabled={!step1Valid} 
                    style={styles.submitBtn(!step1Valid)}
                  >
                    NEXT STEP →
                  </button>
                </div>
              ) : (
                <div style={{ animation: 'slideIn 0.3s ease' }}>
                  {/* Email */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Email Address</label>
                    <div style={styles.inputWrapper(focusedField === 'email', touched.email && !emailValid)}>
                      <input
                        type="email"
                        className="register-input"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setError(''); }}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => { setFocusedField(''); touch('email'); }}
                        style={styles.input}
                      />
                    </div>
                    {touched.email && !emailValid && (
                      <div style={styles.fieldError}>• Please enter a valid email</div>
                    )}
                  </div>

                  {/* Password */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Password</label>
                    <div style={styles.inputWrapper(focusedField === 'password', touched.password && !passwordValid)}>
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="register-input"
                        placeholder="Strong Password"
                        value={password}
                        onChange={(e) => { setPassword(e.target.value); setError(''); }}
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => { setFocusedField(''); touch('password'); }}
                        style={styles.input}
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowPassword(!showPassword)} 
                        style={styles.eyeBtn}
                        tabIndex={-1}
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                    
                    {/* Password Strength Indicator */}
                    <div style={styles.strengthWrapper}>
                      <div style={styles.strengthTrack}>
                        <div style={{
                          ...styles.strengthFill,
                          width: `${passwordStrength.percent}%`,
                          backgroundColor: passwordStrength.color,
                        }} />
                      </div>
                      <span style={{ ...styles.strengthText, color: passwordStrength.color }}>
                        {passwordStrength.label}
                      </span>
                    </div>
                    
                    {touched.password && !passwordValid && (
                      <div style={styles.fieldError}>
                        • Min 8 chars with uppercase, lowercase, number & symbol
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Confirm Password</label>
                    <div style={styles.inputWrapper(focusedField === 'confirm', touched.confirm && !confirmMatches)}>
                      <input
                        type={showConfirm ? 'text' : 'password'}
                        className="register-input"
                        placeholder="Repeat Password"
                        value={confirm}
                        onChange={(e) => { setConfirm(e.target.value); setError(''); }}
                        onFocus={() => setFocusedField('confirm')}
                        onBlur={() => { setFocusedField(''); touch('confirm'); }}
                        style={styles.input}
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowConfirm(!showConfirm)} 
                        style={styles.eyeBtn}
                        tabIndex={-1}
                      >
                        {showConfirm ? '🙈' : '👁️'}
                      </button>
                    </div>
                    {touched.confirm && !confirmMatches && (
                      <div style={styles.fieldError}>• Passwords do not match</div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <button 
                      type="button" 
                      onClick={() => setCurrentStep(1)} 
                      style={styles.backBtn}
                    >
                      ← BACK
                    </button>
                    <button 
                      type="submit" 
                      disabled={!step2Valid || loading} 
                      style={styles.submitBtn(!step2Valid || loading)}
                    >
                      {loading ? (
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          <span style={styles.spinner}></span>
                          CREATING...
                        </span>
                      ) : (
                        'REGISTER'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
            
            <div style={styles.footerLink}>
              Already have an account?{' '}
              <Link to="/login" style={{ color: '#81e6d9', fontWeight: 600, textDecoration: 'none' }}>
                Login Here
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const styles = {
  leftPanel: {
    width: '55%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1,
  },
  rightPanel: {
    width: '30%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 2,
  },
  illustrationContainer: {
    width: '100%',
    maxWidth: '600px',
    padding: '40px',
    textAlign: 'center',
  },
  heroImage: {
    width: '85%',
    maxWidth: '500px',
    height: 'auto',
    animation: 'float 7s ease-in-out infinite',
    marginBottom: '20px',
    filter: 'drop-shadow(0 20px 30px rgba(0,0,0,0.3))',
  },
  heroTextContainer: {
    animation: 'slideUp 0.8s ease-out',
  },
  heroTitle: {
    color: '#fff',
    fontSize: '32px',
    fontWeight: '700',
    marginBottom: '20px',
  },
  benefitsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '10px',
  },
  benefitTag: {
    background: 'rgba(255,255,255,0.1)',
    color: '#81e6d9',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500',
    border: '1px solid rgba(129, 230, 217, 0.2)',
  },
  
  // Form Card
  formCard: {
    width: '100%',
    maxWidth: '450px',
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: '30px',
    padding: '40px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    transition: 'all 0.5s ease',
    position: 'relative',
    overflow: 'hidden',
  },
  headerSection: {
    marginBottom: '20px',
  },
  headerTitle: {
    color: '#fff',
    fontSize: '32px',
    fontWeight: '600',
    margin: 0,
    letterSpacing: '0.5px',
  },
  headerLine: {
    width: '40px',
    height: '4px',
    background: '#81e6d9',
    borderRadius: '2px',
    marginTop: '5px',
    marginBottom: '8px',
  },
  headerSubtitle: {
    color: '#a0aec0',
    fontSize: '15px',
  },
  stepContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '25px',
    background: 'rgba(0,0,0,0.2)',
    padding: '5px',
    borderRadius: '10px',
  },
  stepActive: {
    background: 'linear-gradient(135deg, #81e6d9 0%, #4fd1c5 100%)',
    color: '#1a202c',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '700',
  },
  stepCompleted: {
    background: 'rgba(72, 187, 120, 0.3)',
    color: '#68d391',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '700',
  },
  stepInactive: {
    color: '#718096',
    padding: '6px 12px',
    fontSize: '12px',
  },
  stepLine: {
    flex: 1,
    height: '1px',
    background: '#4a5568',
    margin: '0 10px',
  },
  inputGroup: {
    marginBottom: '15px',
  },
  label: {
    color: '#cbd5e0',
    fontSize: '12px',
    marginBottom: '6px',
    display: 'block',
    fontWeight: '500',
  },
  inputWrapper: (focused, error) => ({
    background: 'rgba(0, 0, 0, 0.25)',
    borderRadius: '12px',
    border: error ? '1px solid #e53e3e' : focused ? '1px solid #81e6d9' : '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.3s ease',
  }),
  input: {
    width: '100%',
    padding: '14px',
    background: 'transparent',
    border: 'none',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
  },
  eyeBtn: {
    background: 'transparent',
    border: 'none',
    color: '#a0aec0',
    padding: '0 15px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  fieldError: {
    color: '#fc8181',
    fontSize: '11px',
    marginTop: '4px',
  },
  submitBtn: (disabled) => ({
    flex: 1,
    padding: '14px',
    marginTop: '15px',
    background: disabled ? '#4a5568' : 'linear-gradient(135deg, #81e6d9 0%, #4fd1c5 100%)',
    color: disabled ? '#a0aec0' : '#1a202c',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: disabled ? 'not-allowed' : 'pointer',
    letterSpacing: '1px',
    boxShadow: disabled ? 'none' : '0 4px 14px 0 rgba(129, 230, 217, 0.39)',
    transition: 'all 0.3s ease',
    width: '100%',
  }),
  backBtn: {
    width: '40%',
    padding: '14px',
    marginTop: '15px',
    background: 'transparent',
    border: '1px solid #4a5568',
    color: '#cbd5e0',
    borderRadius: '12px',
    fontSize: '13px',
    cursor: 'pointer',
    fontWeight: '600',
    transition: 'all 0.3s ease',
  },
  spinner: {
    width: '14px',
    height: '14px',
    border: '2px solid #1a202c',
    borderTopColor: 'transparent',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
  },
  footerLink: {
    textAlign: 'center',
    marginTop: '20px',
    fontSize: '13px',
    color: '#a0aec0',
  },
  errorBox: {
    background: 'rgba(229, 62, 62, 0.2)',
    color: '#feb2b2',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: '1px solid rgba(229, 62, 62, 0.3)',
  },
  successBox: {
    background: 'rgba(72, 187, 120, 0.2)',
    color: '#9ae6b4',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    border: '1px solid rgba(72, 187, 120, 0.3)',
  },
  strengthWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginTop: '8px',
  },
  strengthTrack: {
    flex: 1,
    height: '4px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '2px',
    overflow: 'hidden',
  },
  strengthFill: {
    height: '100%',
    borderRadius: '2px',
    transition: 'all 0.4s ease',
  },
  strengthText: {
    fontSize: '10px',
    fontWeight: '600',
    minWidth: '65px',
    textAlign: 'right',
  },
  
  // ✅ Success Overlay Styles
  successOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'rgba(26, 43, 60, 0.95)',
    zIndex: 10,
    borderRadius: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(10px)',
  },
  successCard: {
    textAlign: 'center',
    color: 'white',
    animation: 'slideUp 0.5s ease',
    padding: '20px',
  },
  checkIcon: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #68d391 0%, #48bb78 100%)',
    color: '#1a202c',
    fontSize: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    animation: 'checkmark 0.5s ease',
    boxShadow: '0 4px 20px rgba(72, 187, 120, 0.4)',
  },
  successTitle: {
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '8px',
    color: '#fff',
  },
  successSubtitle: {
    fontSize: '14px',
    color: '#a0aec0',
    marginBottom: '15px',
  },
  emailBadge: {
    marginTop: '15px',
    padding: '12px 20px',
    background: 'rgba(129, 230, 217, 0.2)',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#81e6d9',
    border: '1px solid rgba(129, 230, 217, 0.3)',
  },
};